import {
  Textarea,
  Button,
  LoadingOverlay,
  Stack,
  Group,
  Grid,
  Text,
  Title,
  Modal,
  Divider,
  Center,
  MultiSelect,
  Tooltip,
  Loader,
} from '@mantine/core';
import { useTranslation } from 'react-i18next';
import { introLetterPol, introLetterEng, PROPOSALACTIONS } from '../../reducers/proposalReducer';
import { IconRotate2, IconInfoCircle } from '@tabler/icons-react';
import { useEffect, useState, useRef } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { fetchExpertise } from '../../../../services/proposalsRoutes';
import ExpandingTextArea from '../../../../components/ExpandingTextArea';
import { useProposal } from '../../context/ProposalContext';
import FileDropZone from '../../PreProposalGeneration/FileDropZone';
import CaseInfo from './CaseOverview';
import SaveProposal from '../Buttons/SaveProposal';
import { useGeneration } from '@/hooks/useGeneration';
import { defaultFeeTextEn, defaultFeeTextPl } from '../SecondTab/defaultFeeText';

export default function FirstTab() {
  const { proposal, dispatchProposal, otherData, dispatchOtherData, userFeedback, dispatchUserFeedback } = useProposal();
  const { input, sector, intro, language } = proposal;
  const { t } = useTranslation();
  const { getAccessTokenSilently } = useAuth0();

  const [isLLMLoading, setIsLLMLoading] = useState<boolean>(false);
  const [resetModalOpened, setResetModalOpened] = useState(false);
  const [showRegenerateButton, setShowRegenerateButton] = useState(false);
  const [confirmModalOpened, setConfirmModalOpened] = useState(false);
  const [isRegenerating, setIsRegenerating] = useState(false);
  const [originalLanguage, setOriginalLanguage] = useState<string>('');
  const prevLanguageRef = useRef<string>(language);

  const { lawyers } = proposal;

  const { generateAll, cancelField, cancelAll, withService } = useGeneration({
    dispatchProposal,
    dispatchUserFeedback,
    dispatchOtherData,
  });

  useEffect(() => {
    async function getExpertise() {
      const accessToken = await getAccessTokenSilently();
      const response = await fetchExpertise(accessToken);
      dispatchOtherData({
        field: 'expertise',
        value: ['Any', ...response],
      });
    }
    getExpertise();
  }, []);

  // Update introDefault on language change
  useEffect(() => {
    const updateIntroDefault = () => {
      if (language === 'en' && intro === introLetterPol) {
        dispatchProposal({
          type: PROPOSALACTIONS.SET_FIELD,
          field: 'intro',
          value: introLetterEng,
        });
      } else if (language === 'pl' && intro === introLetterEng) {
        dispatchProposal({
          type: PROPOSALACTIONS.SET_FIELD,
          field: 'intro',
          value: introLetterPol,
        });
      }
    };
    updateIntroDefault();
  }, [language]);

  // Track the original language when content is first generated
  useEffect(() => {
    if (!originalLanguage && (proposal.scope || proposal.assumptions || proposal.lawyers?.length > 0 || proposal.experience?.length > 0)) {
      setOriginalLanguage(language);
    }
  }, [originalLanguage, language, proposal.scope, proposal.assumptions, proposal.lawyers, proposal.experience]);

  // Track language changes to show regenerate button
  useEffect(() => {
    if (originalLanguage && language !== originalLanguage) {
      setShowRegenerateButton(true);
    } else if (originalLanguage && language === originalLanguage) {
      setShowRegenerateButton(false);
    }
  }, [language, originalLanguage]);

  const handleRegenerateConfirm = async () => {
    setIsRegenerating(true);
    
    // First, wipe all generated content
    dispatchProposal({
      type: PROPOSALACTIONS.SET_FIELD,
      field: 'scope',
      value: '',
    });
    dispatchProposal({
      type: PROPOSALACTIONS.SET_FIELD,
      field: 'assumptions',
      value: '',
    });
    dispatchProposal({
      type: PROPOSALACTIONS.SET_FIELD,
      field: 'lawyers',
      value: [],
    });
    dispatchProposal({
      type: PROPOSALACTIONS.SET_FIELD,
      field: 'experience',
      value: [],
    });
    dispatchProposal({
      type: PROPOSALACTIONS.SET_FIELD,
      field: 'specialRoles',
      value: {},
    });
    
    // Set default fee text based on the target language
    const defaultFees = language === 'en' ? defaultFeeTextEn : defaultFeeTextPl;
    dispatchProposal({
      type: PROPOSALACTIONS.SET_FIELD,
      field: 'fees',
      value: defaultFees,
    });
    
    const config = {
      input: proposal.input,
      sector: proposal.sector,
      documentLanguage: language,
      constraints: proposal.constraints,
      proposal: {
        ...proposal,
        scope: '',
        assumptions: '',
        lawyers: [],
        experience: [],
        specialRoles: {},
        fees: defaultFees,
      },
    };

    try {
      await generateAll(config);
      
      // Update the original language to the new language after successful regeneration
      setOriginalLanguage(language);
    } catch (error) {
      console.error('Error during regeneration:', error);
    } finally {
      setIsRegenerating(false);
      setShowRegenerateButton(false);
      setConfirmModalOpened(false);
    }
  };

  const handleResetIntro = () => {
    dispatchProposal({
      type: PROPOSALACTIONS.SET_FIELD,
      field: 'intro',
      value: language === 'en' ? introLetterEng : introLetterPol,
    });
    setResetModalOpened(false);
  };

  return (
    <Stack p="md" mih="600px">
      {/* <div style={styles.flexRow}>
        <Title order={3}>{t('caseInfo')}</Title>
        <Tooltip label={t('provideCaseInfo')} withArrow multiline w={220} position="right">
          <IconInfoCircle size={20} color="var(--mantine-primary-color-filled)" />
        </Tooltip>
      </div> */}

      <Grid columns={13}>
        {/* Left Column - Case Info */}
        <Grid.Col span={6}>
          <Stack>
            <CaseInfo 
              showRegenerateButton={showRegenerateButton}
              onRegenerateClick={() => setConfirmModalOpened(true)}
            />
            <Title order={4}>{t('caseInfo')}</Title>

            <div style={{ position: 'relative' }}>
              <FileDropZone
                onFilesProcessed={(summaries) => {
                  dispatchProposal({
                    type: PROPOSALACTIONS.SET_FIELD,
                    field: 'input',
                    value:
                      proposal.input.length > 0 ? proposal.input + '\n\n' + summaries : summaries,
                  });
                }}
              />
            </div>
            <div style={{ position: 'relative' }}>
              <LoadingOverlay
                visible={isLLMLoading}
                zIndex={1}
                overlayProps={{ radius: 'sm', blur: 2 }}
              />
              <ExpandingTextArea
                value={input}
                placeholder={t('addYourInputTextHere')}
                onChange={(e) =>
                  dispatchProposal({
                    type: PROPOSALACTIONS.SET_FIELD,
                    field: 'input',
                    value: e.target.value,
                  })
                }
              />
            </div>
          </Stack>
        </Grid.Col>

        {/* Vertical Divider - yep, you do need this double Center to get the divider to line up properly */}
        <Grid.Col span={1}>
          <Center h="100%" w="100%">
            <Center h="90%" w="100%">
              <Divider orientation="vertical" h="100%" />
            </Center>
          </Center>
        </Grid.Col>

        {/* Right Column - Introduction */}
        <Grid.Col span={6}>
          <Stack>
            <Group justify="space-between">
              <Title order={4}>{t('introduction')}</Title>
              <Button
                variant="light"
                color="blue"
                size="xs"
                onClick={() => setResetModalOpened(true)}
                leftSection={<IconRotate2 size={14} />}
              >
                {t('reset')}
              </Button>
            </Group>
            <Textarea
              value={intro}
              placeholder={t('addYourIntroHere')}
              onChange={(e) => {
                dispatchProposal({
                  type: PROPOSALACTIONS.SET_FIELD,
                  field: 'intro',
                  value: e.target.value,
                });
              }}
              variant="filled"
              autosize
              minRows={10}
            />
            <Group gap="xs" align="center">
              <Title order={5}>{t('letterSenders')}</Title>
              <Tooltip
                label={t('letterSendersTooltip')}
                position="top"
                withArrow
                maw={300}
                multiline
              >
                <IconInfoCircle size={18} color="#228be6" style={{ cursor: 'help' }} />
              </Tooltip>
            </Group>
            <MultiSelect
              data={lawyers
                .filter((lawyer) => !proposal.letterSenders.includes(lawyer.name))
                .map((lawyer) => ({ value: lawyer.name, label: lawyer.name }))}
              value={proposal.letterSenders}
              onChange={(value) => {
                dispatchProposal({
                  type: PROPOSALACTIONS.SET_FIELD,
                  field: 'letterSenders',
                  value,
                });
              }}
            />
          </Stack>
        </Grid.Col>
      </Grid>
      <Group justify="flex-end">
        <SaveProposal />
      </Group>

      {/* Reset Modal */}
      <Modal
        opened={resetModalOpened}
        onClose={() => setResetModalOpened(false)}
        title={t('resetIntroduction')}
      >
        <Text>{t('resetIntroductionConfirmation')}</Text>
        <Group justify="flex-end" mt="md">
          <Button variant="light" onClick={() => setResetModalOpened(false)}>
            {t('cancel')}
          </Button>
          <Button color="red" onClick={handleResetIntro}>
            {t('reset')}
          </Button>
        </Group>
      </Modal>

      {/* Regeneration Confirmation Modal */}
      <Modal
        opened={confirmModalOpened}
        onClose={() => setConfirmModalOpened(false)}
        title={t('confirmRegeneration')}
      >
        <Text>
          {t('regenerateWarning')}
        </Text>
        <Group justify="flex-end" mt="md">
          <Button variant="light" onClick={() => setConfirmModalOpened(false)} disabled={isRegenerating}>
            {t('cancel')}
          </Button>
          <Button 
            color="cyan" 
            onClick={handleRegenerateConfirm} 
            disabled={isRegenerating}
            leftSection={isRegenerating ? <Loader size="xs" /> : undefined}
          >
            {isRegenerating ? t('loading') : t('confirm')}
          </Button>
        </Group>
      </Modal>
    </Stack>
  );
}
